import { useCallback, useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { KeyRow, Panel, Stat, TabBar } from "./ui";
import { TikTokEventsPanel } from "./TikTokEventsPanel";
import { AlertTriangle, ExternalLink, Rocket, Send, ShieldCheck, TrendingUp, Users, X } from "lucide-react";

const MARKETING_FIELDS = [
  ["ga_measurement_id", "Google Analytics 4", "G-XXXXXXXXXX", "Copy from GA4 → Admin → Data streams → Web → Measurement ID."],
  ["gtm_container_id", "Google Tag Manager", "GTM-XXXXXX", "Optional. Add this alongside GA4 if you use GTM for extra tags."],
  ["google_ads_conversion_id", "Google Ads conversion ID", "AW-123456789", "The AW- prefix from your conversion action."],
  ["google_ads_purchase_label", "Google Ads purchase label", "abcXYZ1234", "The label of the conversion action tracking a purchase."],
  ["meta_pixel_id", "Meta / Facebook Pixel", "1234567890", "From Events Manager → Pixel ID."],
  ["linkedin_partner_id", "LinkedIn Insight Tag", "123456", "From LinkedIn Campaign Manager → Insight Tag."],
  ["tiktok_pixel_id", "TikTok Pixel", "CXXXXXXXXXXXXXX", "TikTok Ads Manager → Events → Web Events → Pixel ID."],
  ["snapchat_pixel_id", "Snapchat Pixel", "abcd-1234-…", "Snapchat Ads Manager → Events Manager → Pixel ID."],
  ["openai_ads_pixel_id", "ChatGPT Ads pixel (OpenAI)", "px_abc123…", "OpenAI Ads Manager → Conversions → create a web data source → Pixel ID."],
];

const SOCIAL_FIELDS = [
  ["social_instagram", "Instagram", "https://instagram.com/fleurinelle"],
  ["social_facebook", "Facebook", "https://facebook.com/fleurinelle"],
  ["social_tiktok", "TikTok", "https://www.tiktok.com/@fleurinelle"],
  ["social_youtube", "YouTube", "https://youtube.com/@fleurinelle"],
  ["social_linkedin", "LinkedIn", "https://linkedin.com/company/fleurinelle"],
  ["social_snapchat", "Snapchat", "https://snapchat.com/add/fleurinelle"],
  ["social_x", "X (Twitter)", "https://x.com/fleurinelle"],
  ["social_pinterest", "Pinterest", "https://pinterest.com/fleurinelle"],
  ["social_whatsapp_channel", "WhatsApp Channel", "https://whatsapp.com/channel/…"],
];

export default function MarketingPage() {
  const [tab, setTab] = useState("marketing");
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState("");
  const [reset, setReset] = useState(null);

  const load = useCallback(() => api.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => setMsg(errText(e))), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    setMsg("");
    try {
      await api.put("/admin/settings", fields);
      setMsg("Saved.");
      await load();
    } catch (e) { setMsg(errText(e)); }
  };

  if (!settings) return <p className="text-sm text-muted" data-testid="marketing-loading">Loading…</p>;

  const openReset = async () => {
    setMsg("");
    try {
      const { data } = await api.get("/admin/reset/production/preview");
      setReset({ ...data, phrase: "", keep_florist_id: data.florists[0]?.id || "" });
    } catch (e) { setMsg(errText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-marketing-page">
      <div>
        <h1 className="font-display text-3xl text-ink">Marketing, socials & launch</h1>
        <p className="text-sm text-muted">Ad pixels, Gleap widgets for buyers and florists, social media links in the footer, and the launch-day reset.</p>
      </div>
      {msg ? <p className="text-sm text-brass" data-testid="marketing-message">{msg}</p> : null}

      <TabBar testid="marketing-tabs" value={tab} onChange={setTab}
              tabs={[["marketing", "Analytics & pixels"], ["gleap", "Gleap support"], ["socials", "Social links"], ["seo", "SEO & feeds"], ["reset", "Launch reset"]]} />

      {tab === "marketing" ? (
        <MarketingTab settings={settings} onSave={patch} />
      ) : null}

      {tab === "gleap" ? (
        <GleapTab settings={settings} onSave={patch} />
      ) : null}

      {tab === "socials" ? (
        <SocialsTab settings={settings} onSave={patch} />
      ) : null}

      {tab === "seo" ? (
        <SeoTab />
      ) : null}

      {tab === "reset" ? (
        <ResetTab settings={settings} onOpen={openReset} />
      ) : null}

      {reset ? (
        <ResetModal reset={reset} setReset={setReset} onDone={() => { setReset(null); load(); }} setMsg={setMsg} />
      ) : null}
    </div>
  );
}

function MetaPanel({ settings, onSave }) {
  const [capi, setCapi] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [token, setToken] = useState("");
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState({
    meta_test_event_code: settings.meta_test_event_code || "",
    facebook_domain_verification: settings.facebook_domain_verification || "",
    meta_pixel_pre_consent: settings.meta_pixel_pre_consent || "off",
    meta_capi_enabled: settings.meta_capi_enabled || "on",
    meta_catalog_id: settings.meta_catalog_id || "",
    meta_catalog_push_enabled: settings.meta_catalog_push_enabled || "on",
  });
  const load = useCallback(() => {
    api.get("/admin/settings/meta-capi").then((r) => setCapi(r.data)).catch(() => {});
    api.get("/admin/settings/meta-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveToken = async () => {
    setMsg("");
    try {
      const { data } = await api.put("/admin/settings/meta-capi", { access_token: token.trim() });
      setCapi(data); setToken(""); setMsg("Conversions API token saved.");
    } catch (e) { setMsg(errText(e)); }
  };
  const removeToken = async () => {
    try { const { data } = await api.delete("/admin/settings/meta-capi"); setCapi(data); setMsg("Token removed."); }
    catch (e) { setMsg(errText(e)); }
  };
  const testEvent = async () => {
    setMsg("Sending…");
    try {
      const { data } = await api.post("/admin/settings/meta-capi/test");
      setMsg(data.ok ? "Test event accepted by Meta — check Events Manager → Test events."
                     : `Meta refused it: ${data.reason || data.status}`);
    } catch (e) { setMsg(errText(e)); }
  };

  const site = window.location.origin;
  const checkoutUrl = `${site}/en/checkout?products={product.retailer_id}:1&coupon={coupon_code}`;

  return (
    <Panel title="Meta: pixel, Conversions API & Commerce" testid="meta-panel"
           action={<ShieldCheck size={16} className="text-brass" />}>
      <p className="text-xs text-muted">
        The pixel now loads on the first pageview and holds its events until the cookie banner is accepted
        (Meta's own consent API). Server-side purchases are sent through the Conversions API with the same
        event id, so Meta counts one conversion, not two.
      </p>
      {msg ? <p className="text-xs text-brass" data-testid="meta-message">{msg}</p> : null}

      <div className="space-y-2 border-t border-brass-light pt-3">
        <p className="text-xs text-ink font-medium">Conversions API access token</p>
        <p className="text-xs text-muted" data-testid="meta-capi-status">
          {capi?.token_configured ? `token saved (····${capi.token_last4})` : "no token yet"} · pixel {capi?.pixel_id || "not set"} · Graph {capi?.graph_version || "—"}
          {capi?.ready ? " · ready" : " · incomplete"}
        </p>
        <input className="field" placeholder="System-user access token (EAA…)" autoComplete="off" data-testid="meta-capi-token"
               value={token} onChange={(e) => setToken(e.target.value)} />
        <p className="text-xs text-muted">Events Manager → your dataset → Settings → Conversions API → Generate access token. The token stays on the server and is never sent to the browser.</p>
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary text-xs px-4" data-testid="meta-capi-save" disabled={token.trim().length < 20} onClick={saveToken}>Save token</button>
          {capi?.token_configured ? <button className="btn-ghost text-xs px-3" data-testid="meta-capi-test" onClick={testEvent}>Send test event</button> : null}
          {capi?.token_configured ? <button className="btn-ghost text-xs px-3" data-testid="meta-capi-remove" onClick={removeToken}>Remove token</button> : null}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 border-t border-brass-light pt-3">
        <TextField label="Meta Catalog ID" placeholder="1234567890123456" testid="meta-catalog-id"
                   hint="Commerce Manager → Catalogue → Settings → Catalogue ID. Enables real-time push (publish → shoppable in seconds)."
                   value={draft.meta_catalog_id} onChange={(v) => setDraft({ ...draft, meta_catalog_id: v })} />
        <label className="block text-xs">
          <span className="text-ink font-medium">Real-time catalogue push</span>
          <select className="field mt-1" data-testid="meta-catalog-push" value={draft.meta_catalog_push_enabled}
                  onChange={(e) => setDraft({ ...draft, meta_catalog_push_enabled: e.target.value })}>
            <option value="on">On — push every publish, edit, pause and archive</option>
            <option value="off">Off — rely on the scheduled feed fetch only</option>
          </select>
          <span className="text-muted block mt-1" data-testid="meta-catalog-status">
            {catalog?.ready ? `ready — catalogue ${catalog.catalog_id} · ${catalog.synced_items} item(s) in sync` : catalog?.catalog_id ? (catalog.push_enabled ? "catalogue set — add the Conversions API token to sync" : "catalogue set but push disabled") : "no catalogue id yet"}
          </span>
          {catalog?.catalog_id && catalog?.last_push ? (
            <span className={`block mt-1 ${catalog.last_push.ok ? "text-muted" : "text-danger"}`} data-testid="meta-catalog-last-push">
              Last push {catalog.last_push.at?.slice(0, 16).replace("T", " ")} · {catalog.last_push.count} item(s) · {catalog.last_push.ok ? "accepted" : `refused (${catalog.last_push.status || "error"})`}
            </span>
          ) : null}
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-3 border-t border-brass-light pt-3">
        <TextField label="Test event code" placeholder="TEST12345" testid="meta-test-event-code"
                   hint="Only while testing in Events Manager → Test events. Clear it before going live."
                   value={draft.meta_test_event_code} onChange={(v) => setDraft({ ...draft, meta_test_event_code: v })} />
        <TextField label="Facebook domain verification" placeholder="abc123def456…" testid="meta-domain-verification"
                   hint="Business Settings → Brand safety → Domains. Added as a meta tag on every storefront page."
                   value={draft.facebook_domain_verification} onChange={(v) => setDraft({ ...draft, facebook_domain_verification: v })} />
        <label className="block text-xs">
          <span className="text-ink font-medium">Fire the pixel before consent</span>
          <select className="field mt-1" data-testid="meta-pre-consent" value={draft.meta_pixel_pre_consent}
                  onChange={(e) => setDraft({ ...draft, meta_pixel_pre_consent: e.target.value })}>
            <option value="off">No — wait for the cookie banner (EU-safe)</option>
            <option value="on">Yes — fire immediately on every visit</option>
          </select>
          <span className="text-muted block mt-1">Turn this on only if your traffic is outside the EU/UK.</span>
        </label>
        <label className="block text-xs">
          <span className="text-ink font-medium">Conversions API</span>
          <select className="field mt-1" data-testid="meta-capi-enabled" value={draft.meta_capi_enabled}
                  onChange={(e) => setDraft({ ...draft, meta_capi_enabled: e.target.value })}>
            <option value="on">On — send server-side purchases</option>
            <option value="off">Off</option>
          </select>
          <span className="text-muted block mt-1">Server events survive ad blockers and iOS tracking limits.</span>
        </label>
      </div>
      <button className="btn-primary w-full sm:w-fit" data-testid="meta-settings-save" onClick={() => onSave(draft)}>Save Meta settings</button>

      <div className="border-t border-brass-light pt-3 space-y-1">
        <p className="text-xs text-ink font-medium">Commerce checkout URL (Meta shop)</p>
        <p className="text-xs text-muted">Paste this in Meta Commerce Manager → Checkout URL. The storefront reads
          <code> products</code> and <code> coupon</code> and fills the basket automatically.</p>
        <code className="block text-[11px] bg-plaster-deep p-2 break-all" data-testid="meta-checkout-url">{checkoutUrl}</code>
        <p className="text-xs text-muted">Catalogue product IDs are the same IDs published in the product feed, so no mapping is needed.</p>
      </div>
    </Panel>
  );
}

function TextField({ label, hint, placeholder, value, onChange, testid, type = "text" }) {
  return (
    <label className="block text-xs">
      <span className="text-ink font-medium">{label}</span>
      <input type={type} className="field mt-1" placeholder={placeholder} value={value || ""} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
      {hint ? <span className="text-muted block mt-1">{hint}</span> : null}
    </label>
  );
}

function MarketingTab({ settings, onSave }) {
  const [draft, setDraft] = useState(() => ({ ...MARKETING_FIELDS.reduce((a, [k]) => ({ ...a, [k]: settings[k] || "" }), {}),
                                              tiktok_pixel_pre_consent: settings.tiktok_pixel_pre_consent || "on" }));
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const configured = MARKETING_FIELDS.filter(([k]) => (settings[k] || "").trim());
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <Panel title="Ad platform pixels & analytics" testid="marketing-tags-panel"
               action={<TrendingUp size={16} className="text-brass" />}>
          <p className="text-xs text-muted">
            These fields power tracking on every storefront page. Standard events fired: <code>view_item</code>,
            <code> add_to_cart</code>, <code>begin_checkout</code>, <code>purchase</code> — with product IDs, value and currency.
            Leave any field blank to skip that platform.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {MARKETING_FIELDS.map(([key, label, ph, hint]) => (
              <TextField key={key} label={label} placeholder={ph} hint={hint} value={draft[key]}
                         onChange={(v) => set(key, v)} testid={`marketing-${key}`} />
            ))}
          </div>
          <label className="block text-xs md:w-1/2">
            <span className="text-ink font-medium">Fire the TikTok pixel before consent</span>
            <select className="field mt-1" data-testid="tiktok-pre-consent" value={draft.tiktok_pixel_pre_consent}
                    onChange={(e) => set("tiktok_pixel_pre_consent", e.target.value)}>
              <option value="on">Yes — fire immediately on every visit</option>
              <option value="off">No — wait for the cookie banner (EU-safe)</option>
            </select>
            <span className="text-muted block mt-1">When off, TikTok only receives events from visitors who click Accept.</span>
          </label>
          <button className="btn-primary w-full sm:w-fit" data-testid="marketing-save" onClick={() => onSave(draft)}>Save pixels</button>
        </Panel>
        <MetaPanel settings={settings} onSave={onSave} />
        <TikTokEventsPanel settings={settings} onSave={onSave} TextField={TextField} />
        <OpenAiAdsPanel settings={settings} onSave={onSave} />
        <ConversionValuePanel settings={settings} onSave={onSave} />
      </div>
      <Panel title="Live pixels" testid="marketing-status">
        {configured.length === 0 ? <p className="text-xs text-muted">Nothing configured yet.</p> : null}
        {configured.map(([key, label]) => (
          <KeyRow key={key} label={label}><code className="text-xs">····{(settings[key] || "").slice(-6)}</code></KeyRow>
        ))}
        <div className="pt-3 border-t border-brass-light text-xs text-muted space-y-1">
          <p>Verify with:</p>
          <a className="underline decoration-brass" href="https://chrome.google.com/webstore/detail/google-tag-assistant-lega/kejbdjndbnbjgmefkgdddjlbokphdefk" target="_blank" rel="noreferrer">Google Tag Assistant <ExternalLink size={10} className="inline" /></a>
          <br/>
          <a className="underline decoration-brass" href="https://chrome.google.com/webstore/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc" target="_blank" rel="noreferrer">Meta Pixel Helper <ExternalLink size={10} className="inline" /></a>
        </div>
      </Panel>
    </div>
  );
}

function GleapTab({ settings, onSave }) {
  const [customers, setCustomers] = useState(settings.gleap_customers_key || "");
  const [florists, setFlorists] = useState(settings.gleap_florists_key || "");
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Panel title="Buyer support widget" testid="gleap-customers-panel" action={<Users size={16} className="text-brass" />}>
        <p className="text-xs text-muted">
          The Gleap widget loaded on every public page. Create a <b>Gleap project called "Fleurinelle — Customers"</b>,
          then paste its SDK key here. Buyer name and email are auto-attached when they're signed in.
        </p>
        <TextField label="SDK key" placeholder="1234567890abcdef" value={customers} onChange={setCustomers} testid="gleap-customers-key" />
        <button className="btn-primary" data-testid="gleap-customers-save" onClick={() => onSave({ gleap_customers_key: customers })}>Save</button>
        <a className="underline decoration-brass text-xs" href="https://app.gleap.io/projects" target="_blank" rel="noreferrer">
          Open Gleap dashboard <ExternalLink size={10} className="inline" />
        </a>
      </Panel>
      <Panel title="Florist support widget" testid="gleap-florists-panel" action={<ShieldCheck size={16} className="text-brass" />}>
        <p className="text-xs text-muted">
          A <b>separate Gleap project called "Fleurinelle — Florists"</b> so partner tickets never mix with buyer chats.
          Loaded only on <code>/florist/*</code> routes, with the florist owner's identity attached.
        </p>
        <TextField label="SDK key" placeholder="1234567890abcdef" value={florists} onChange={setFlorists} testid="gleap-florists-key" />
        <button className="btn-primary" data-testid="gleap-florists-save" onClick={() => onSave({ gleap_florists_key: florists })}>Save</button>
      </Panel>
    </div>
  );
}

function SocialsTab({ settings, onSave }) {
  const [draft, setDraft] = useState(() => SOCIAL_FIELDS.reduce((a, [k]) => ({ ...a, [k]: settings[k] || "" }), {}));
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <Panel title="Social media links" testid="socials-panel">
      <p className="text-xs text-muted">Icons appear in the site footer. Leave a field empty to hide that icon.</p>
      <div className="grid md:grid-cols-2 gap-3">
        {SOCIAL_FIELDS.map(([key, label, ph]) => (
          <TextField key={key} label={label} placeholder={ph} value={draft[key]}
                     onChange={(v) => set(key, v)} testid={`social-${key}`} />
        ))}
      </div>
      <button className="btn-primary w-full sm:w-fit" data-testid="socials-save" onClick={() => onSave(draft)}>Save social links</button>
    </Panel>
  );
}

function RefreshMetaButton() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const run = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/admin/feeds/refresh-meta");
      if (data.reason === "not_configured") setMsg("Add a Catalog ID + CAPI token first.");
      else if (data.reason === "up_to_date") setMsg("Meta is already up to date.");
      else setMsg(data.ok ? `Meta accepted ${data.pushed} bouquet${data.pushed === 1 ? "" : "s"}${data.deleted ? `, removed ${data.deleted}` : ""}.`
                          : `Meta refused ${data.failed} bouquet${data.failed === 1 ? "" : "s"} — see the last push status in the Meta panel.`);
    } catch (e) { setMsg(errText(e)); }
    setBusy(false);
  };
  return (
    <>
      <button className="btn-primary text-xs" onClick={run} disabled={busy}
              data-testid="feed-refresh-meta">
        {busy ? "Pushing to Meta…" : "Refresh Meta feed now"}
      </button>
      {msg ? <span className="text-xs text-muted self-center" data-testid="feed-refresh-meta-msg">{msg}</span> : null}
    </>
  );
}

function OpenAiAdsPanel({ settings, onSave }) {
  const [state, setState] = useState(null);
  const [key, setKey] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get("/admin/settings/openai-ads").then((r) => setState(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const saveKey = async () => {
    setMsg("");
    try { const { data } = await api.put("/admin/settings/openai-ads", { api_key: key.trim() }); setState(data); setKey(""); setMsg("Conversions API key saved."); }
    catch (e) { setMsg(errText(e)); }
  };
  const removeKey = async () => {
    try { const { data } = await api.delete("/admin/settings/openai-ads"); setState(data); setMsg("Key removed."); }
    catch (e) { setMsg(errText(e)); }
  };
  const test = async () => {
    setMsg("Validating…");
    try {
      const { data } = await api.post("/admin/settings/openai-ads/test");
      setMsg(data.ok ? (data.live_page_view?.ok
                          ? "Key validated + one real page_viewed sent — it should appear in Ads Manager → Conversions → Event Stream within a few minutes."
                          : "Key validated, but the live page_viewed was refused: " + (data.live_page_view?.response || data.live_page_view?.reason || ""))
                     : `OpenAI refused it: ${data.reason || data.status} ${data.response || ""}`);
    } catch (e) { setMsg(errText(e)); }
  };

  return (
    <Panel title="ChatGPT Ads: pixel & Conversions API" testid="openai-ads-panel"
           action={<Rocket size={16} className="text-brass" />}>
      <p className="text-xs text-muted">
        Paste the <b>Pixel ID</b> above (ChatGPT Ads pixel) and the <b>Conversions API key</b> here. The browser pixel sends
        <code> page_viewed</code>, <code>contents_viewed</code>, <code>items_added</code>, <code>checkout_started</code> and
        <code> order_created</code>; the server repeats <code>order_created</code> with the same event id, so paid orders are
        counted once even when the browser is blocked. Amounts are sent in minor units with the buyer's real currency —
        that is what lets ChatGPT Ads optimise on revenue and keep your budget on the keywords that convert.
      </p>
      {msg ? <p className="text-xs text-brass" data-testid="openai-ads-message">{msg}</p> : null}
      <p className="text-xs text-muted" data-testid="openai-ads-status">
        {state?.key_configured ? `key saved (····${state.key_last4})` : "no key yet"} · pixel {state?.pixel_id || "not set"}
        {state?.ready ? " · ready" : " · incomplete"}
      </p>
      <input className="field" placeholder="Conversions API key" autoComplete="off" data-testid="openai-ads-key"
             value={key} onChange={(e) => setKey(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary text-xs px-4" data-testid="openai-ads-save" disabled={key.trim().length < 10} onClick={saveKey}>Save key</button>
        {state?.key_configured ? <button className="btn-ghost text-xs px-3" data-testid="openai-ads-test" onClick={test}>Send validation event</button> : null}
        {state?.key_configured ? <button className="btn-ghost text-xs px-3" data-testid="openai-ads-remove" onClick={removeKey}>Remove key</button> : null}
      </div>
      <label className="block text-xs border-t border-brass-light pt-3">
        <span className="text-ink font-medium">Server-side conversions</span>
        <select className="field mt-1" data-testid="openai-ads-enabled" value={settings.openai_ads_capi_enabled || "on"}
                onChange={(e) => onSave({ openai_ads_capi_enabled: e.target.value })}>
          <option value="on">On — send every paid order from the server</option>
          <option value="off">Off — browser pixel only</option>
        </select>
      </label>
      <p className="text-xs text-muted">
        In Ads Manager → Conversions, create a conversion setting for <code>order_created</code> and attach it to your
        campaigns, otherwise the events arrive but no campaign optimises on them.
      </p>
    </Panel>
  );
}

function IndexingPanel() {
  const [state, setState] = useState(null);
  const [gsc, setGsc] = useState("");
  const [bing, setBing] = useState("");
  const [prop, setProp] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/seo/indexing")
    .then((r) => { setState(r.data); setProp(r.data.google?.site_url || ""); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const saveGsc = async () => {
    setMsg("");
    try { const { data } = await api.put("/admin/seo/indexing/google-key", { service_account_json: gsc }); setState(data); setGsc(""); setMsg("Service account saved."); }
    catch (e) { setMsg(errText(e)); }
  };
  const saveBing = async () => {
    setMsg("");
    try { const { data } = await api.put("/admin/seo/indexing/bing-key", { api_key: bing.trim() }); setState(data); setBing(""); setMsg("Bing key saved."); }
    catch (e) { setMsg(errText(e)); }
  };
  const push = async () => {
    setBusy(true); setMsg("Submitting the full sitemap…");
    try {
      const { data } = await api.post("/admin/seo/indexing/push", {});
      setMsg(`${data.url_count} URLs · IndexNow ${data.indexnow?.ok ? "accepted" : data.indexnow?.reason || data.indexnow?.status} · Google ${data.google?.ok ? "sitemap resubmitted" : data.google?.reason || data.google?.status}`);
      load();
    } catch (e) { setMsg(errText(e)); }
    setBusy(false);
  };

  return (
    <Panel title="Push new content to Google & Bing" testid="indexing-panel" action={<Send size={16} className="text-brass" />}>
      <p className="text-xs text-muted">
        After each AI content job the fresh URLs are submitted automatically. Use the button for a full sitemap push.
        Submission notifies search engines; it does not guarantee crawling, indexing or rankings.
        You can also submit the sitemap manually in Google Search Console and Bing Webmaster Tools without API credentials.
      </p>
      {msg ? <p className="text-xs text-brass" data-testid="indexing-message">{msg}</p> : null}
      <button className="btn-primary text-xs px-4" disabled={busy} onClick={push} data-testid="indexing-push-all">
        {busy ? "Pushing…" : "Push the whole sitemap now"}
      </button>
      {state?.last ? (
        <p className="text-[11px] text-muted" data-testid="indexing-last">
          Last push: {state.last.at?.slice(0, 16).replace("T", " ")} · {state.last.url_count} URLs · {state.last.reason}
          {" · IndexNow "}{state.last.indexnow?.ok ? "ok" : state.last.indexnow?.reason || state.last.indexnow?.status}
          {" · Google "}{state.last.google?.ok ? "ok" : state.last.google?.reason || state.last.google?.status}
        </p>
      ) : null}

      <div className="border-t border-brass-light pt-3 space-y-1">
        <p className="text-xs text-ink font-medium" data-testid="indexnow-config-state">IndexNow {state?.indexnow?.ready ? "· configured (key verification required)" : "· not configured"}</p>
        <p className="text-[11px] text-muted">
          Key file must stay reachable at <code className="break-all">{state?.indexnow?.key_location || "—"}</code>
          {" "}(it is shipped with the frontend build).
        </p>
      </div>

      <div className="border-t border-brass-light pt-3 space-y-2">
        <p className="text-xs text-ink font-medium">Google Search Console {state?.google?.ready ? "· ready" : "· needs setup"}</p>
        <p className="text-[11px] text-muted">
          1. Google Cloud → IAM → Service accounts → create one → Keys → <b>JSON</b>. 2. Enable the <b>Search Console API</b>.
          3. In Search Console → Settings → Users and permissions, add{" "}
          <code className="break-all">{state?.google?.service_account || "the service-account e-mail"}</code> as an <b>Owner</b>.
          4. Set the property below exactly as Search Console shows it (<code>sc-domain:fleurinelle.com</code> for a domain property).
        </p>
        <TextField label="Search Console property" placeholder="sc-domain:fleurinelle.com" testid="gsc-site-url"
                   value={prop} onChange={setProp} />
        <div className="flex flex-wrap gap-2">
          <button className="btn-ghost text-xs px-3" data-testid="gsc-site-save"
                  onClick={async () => {
                    setMsg("");
                    try { await api.put("/admin/settings", { gsc_site_url: prop.trim() }); setMsg("Property saved."); load(); }
                    catch (e) { setMsg(errText(e)); }
                  }}>Save property</button>
        </div>
        <textarea className="field text-[11px] font-mono" rows={3} placeholder='{"type":"service_account", …}'
                  data-testid="gsc-json" value={gsc} onChange={(e) => setGsc(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary text-xs px-4" disabled={gsc.trim().length < 40} onClick={saveGsc} data-testid="gsc-save">Save service account</button>
          {state?.google?.configured ? (
            <button className="btn-ghost text-xs px-3" data-testid="gsc-remove"
                    onClick={async () => { const { data } = await api.delete("/admin/seo/indexing/google-key"); setState(data); }}>Remove</button>
          ) : null}
        </div>
      </div>

      <div className="border-t border-brass-light pt-3 space-y-2">
        <p className="text-xs text-ink font-medium">Bing Webmaster API (optional) {state?.bing?.configured ? `· saved ····${state.bing.key_last4}` : ""}</p>
        <input type="password" className="field text-xs" placeholder="Bing Webmaster API key" value={bing} data-testid="bing-key"
               onChange={(e) => setBing(e.target.value)} />
        <button className="btn-primary text-xs px-4" disabled={bing.trim().length < 8} onClick={saveBing} data-testid="bing-save">Save Bing key</button>
      </div>
    </Panel>
  );
}

function ConversionValuePanel({ settings, onSave }) {
  const [draft, setDraft] = useState({
    conversion_value_basis: settings.conversion_value_basis || "margin",
    conversion_value_currency: settings.conversion_value_currency || "EUR",
    conversion_value_floor_mad: settings.conversion_value_floor_mad || "0",
  });
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/conversion-values").then((r) => setRows(r.data.rows)).catch(() => {}); }, [settings]);
  return (
    <Panel title="Conversion value — bid on profit, not turnover" testid="conversion-value-panel"
           action={<TrendingUp size={16} className="text-brass" />}>
      <p className="text-xs text-muted">
        Google Ads, ChatGPT Ads, Meta, TikTok and Snapchat receive this number as the value of a sale, so their
        automated bidding chases the orders that actually earn you money. GA4 always keeps the real revenue, so your
        reporting stays honest. The margin is the selling price minus the partner cost, the card fee and the whole
        discount — partner payouts are never reduced by an offer.
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        <label className="block text-xs">
          <span className="text-ink font-medium">Value sent to ad platforms</span>
          <select className="field mt-1" data-testid="cv-basis" value={draft.conversion_value_basis}
                  onChange={(e) => setDraft({ ...draft, conversion_value_basis: e.target.value })}>
            <option value="margin">Net margin — what Fleurinelle keeps (recommended)</option>
            <option value="gross">Gross order total</option>
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-ink font-medium">Currency</span>
          <select className="field mt-1" data-testid="cv-currency" value={draft.conversion_value_currency}
                  onChange={(e) => setDraft({ ...draft, conversion_value_currency: e.target.value })}>
            <option value="EUR">Always EUR — one scale across every market</option>
            <option value="MAD">Always MAD</option>
            <option value="BUYER">The buyer's own currency</option>
          </select>
        </label>
        <TextField label="Never report less than (MAD)" placeholder="0" testid="cv-floor"
                   hint="Protects bidding when a heavy discount wipes out the margin."
                   value={draft.conversion_value_floor_mad} onChange={(v) => setDraft({ ...draft, conversion_value_floor_mad: v.replace(/[^0-9]/g, "") })} />
      </div>
      <button className="btn-primary w-full sm:w-fit" data-testid="cv-save" onClick={() => onSave(draft)}>Save conversion value</button>
      {rows?.length ? (
        <div className="border-t border-brass-light pt-3">
          <p className="text-xs text-ink font-medium mb-2">Last orders — what each platform received</p>
          <table className="w-full text-xs" data-testid="cv-preview">
            <thead><tr className="smallcaps"><th className="text-start py-1">Order</th><th>Gross</th><th>Margin</th><th>Sent</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.order_number} className="border-t border-brass-light/60" data-testid={`cv-row-${r.order_number}`}>
                  <td className="py-1">{r.order_number}</td>
                  <td className="text-center tabular">{r.total_mad} MAD</td>
                  <td className={`text-center tabular ${r.margin_mad <= 0 ? "text-danger" : ""}`}>{r.margin_mad} MAD</td>
                  <td className="text-center tabular text-brass">{r.conversion_value} {r.conversion_currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Panel>
  );
}

function SeoTab() {
  const site = (typeof window !== "undefined" ? window.location.origin : "");
  const [health, setHealth] = useState(null);
  const [abandoned, setAbandoned] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locale, setLocale] = useState("en");
  const [currency, setCurrency] = useState("EUR");
  const [error, setError] = useState("");

  const run = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const [h, a] = await Promise.all([
        api.get(`/admin/feeds/health?locale=${locale}&currency=${currency}`),
        api.get(`/admin/abandoned/summary`),
      ]);
      setHealth(h.data);
      setAbandoned(a.data);
    } catch (e) { setError(errText(e)); }
    setBusy(false);
  }, [locale, currency]);

  useEffect(() => { run(); }, [run]);

  const links = [
    ["Sitemap (XML)", "/api/sitemap.xml", "Point Google Search Console + Bing Webmaster here."],
    ["Robots", "/api/robots.txt", "Reference this from /robots.txt at your CDN edge."],
    ["Google Merchant XML (EN, EUR)", "/api/feeds/google-merchant.xml?locale=en&currency=EUR", "Merchant Center → Feeds → Scheduled fetch."],
    ["Google Merchant XML (FR, EUR)", "/api/feeds/google-merchant.xml?locale=fr&currency=EUR", "Feed for the French locale."],
    ["Google Merchant XML (MAD)", "/api/feeds/google-merchant.xml?locale=en&currency=MAD", "Local currency feed."],
    ["Meta catalogue CSV", "/api/feeds/meta-catalogue.csv?locale=en&currency=EUR", "Meta Commerce Manager → Catalogues."],
    ["TikTok catalogue CSV", "/api/feeds/tiktok-catalogue.csv?locale=en&currency=EUR", "TikTok Catalog Manager → Add products → Data feed → Scheduled (daily)."],
  ];
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Panel title="Feed health" testid="feed-health-panel">
        <div className="flex flex-wrap gap-2 items-center text-xs">
          <label className="text-muted">Locale
            <select className="field mt-1" value={locale} data-testid="feed-locale"
                    onChange={(e) => setLocale(e.target.value)}>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
              <option value="ar">العربية</option>
            </select>
          </label>
          <label className="text-muted">Currency
            <select className="field mt-1" value={currency} data-testid="feed-currency"
                    onChange={(e) => setCurrency(e.target.value)}>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="MAD">MAD</option>
            </select>
          </label>
          <button className="btn-ghost text-xs ms-auto" onClick={run} disabled={busy}
                  data-testid="feed-refresh">{busy ? "Checking…" : "Refresh"}</button>
        </div>
        {error ? <p className="text-xs text-danger mt-3" data-testid="feed-health-error">{error}</p> : null}
        {!health ? <p className="text-xs text-muted mt-3">Loading feed diagnostics…</p> : (
          <div className="space-y-3 mt-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Catalogue" value={health.total_products} testid="stat-total" />
              <Stat label="Published" value={health.published_products} testid="stat-published" />
              <Stat label="In feed" value={health.feed_rows} testid="stat-feed" />
              <Stat label="Shoppable" value={`${Math.round((health.shopable_ratio || 0) * 100)}%`} testid="stat-shopable" />
            </div>
            {health.invalid_rows > 0 ? (
              <div className="border border-danger/30 bg-danger/5 p-3 text-xs" data-testid="feed-issues">
                <p className="font-medium text-danger flex items-center gap-2">
                  <AlertTriangle size={13} /> {health.invalid_rows} bouquet(s) will be rejected by Meta / Google
                </p>
                <ul className="mt-2 space-y-1 text-muted">
                  {Object.entries(health.issues).map(([key, ids]) => (
                    ids.length ? (
                      <li key={key}>
                        <b>{key.replaceAll("_", " ")}:</b> {ids.slice(0, 5).join(", ")}{ids.length > 5 ? ` +${ids.length - 5} more` : ""}
                      </li>
                    ) : null
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-xs text-brass flex items-center gap-2" data-testid="feed-ok">
                <ShieldCheck size={13} /> Every bouquet is shoppable — Meta and Google will accept the full catalogue.
              </p>
            )}
            <div className="flex flex-wrap gap-2 text-xs">
              <a className="btn-ghost" href={site + health.google_url} target="_blank" rel="noreferrer" data-testid="feed-google-link">Open Google feed <ExternalLink size={10} className="inline ms-1" /></a>
              <a className="btn-ghost" href={site + health.meta_url} target="_blank" rel="noreferrer" data-testid="feed-meta-link">Open Meta feed <ExternalLink size={10} className="inline ms-1" /></a>
              <RefreshMetaButton />
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Abandoned-cart retargeting" testid="abandoned-panel">
        <p className="text-xs text-muted">
          Every Add-to-Cart fires the pixel and a matching server-side event, so ad blockers and iOS don't drop your audience.
          Idle carts (30&nbsp;min – 24&nbsp;h without checkout) get a refreshed <code>AddToCart</code> ping every 15 minutes so Meta keeps them in your retargeting window.
        </p>
        {!abandoned ? <p className="text-xs text-muted">Loading…</p> : (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Stat label="Signals last 24h" value={abandoned.signals_24h} testid="atc-signals-24h" />
            <Stat label="Retargeted last 24h" value={abandoned.retargeted_24h} testid="atc-retargeted-24h" />
            <Stat label="Converted last 24h" value={abandoned.converted_24h} testid="atc-converted-24h" />
            <Stat label="Waiting for sweep" value={abandoned.pending_sweep} testid="atc-pending" />
          </div>
        )}
      </Panel>

      <IndexingPanel />

      <Panel title="Search engines & feed URLs" testid="seo-panel">
        <p className="text-xs text-muted">Every product, city, occasion, corridor is indexed with hreflang and JSON-LD.</p>
        {links.map(([label, path, hint]) => (
          <div key={path} className="border-b border-brass-light py-2">
            <a className="text-sm text-ink underline decoration-brass underline-offset-2" href={site + path} target="_blank" rel="noreferrer" data-testid={`seo-link-${path}`}>{label} <ExternalLink size={11} className="inline" /></a>
            <p className="text-[11px] text-muted">{hint}</p>
          </div>
        ))}
      </Panel>

      <Panel title="Submitting the feeds" testid="merchant-panel">
        <p className="text-xs text-muted">
          <b>Meta:</b> once the Catalog ID and Conversions API token are saved, every change to a title, description, price,
          sale, photo or availability is pushed automatically within minutes, and removed bouquets are deleted from Meta.
          The CSV below can also be added as a scheduled feed (hourly) as a safety net.
          <br /><b>Google, TikTok, Pinterest, Snapchat:</b> they re-read the feed URL on their fetch schedule, which always
          serves the current catalogue. Titles and descriptions are the website copy, without city names.
        </p>
        <ol className="text-xs space-y-2 mt-2">
          <li>1. <b>Meta:</b> Commerce Manager → Catalogue → Settings → copy the Catalogue ID into the Meta panel above.</li>
          <li>2. Optional bulk feed: Commerce Manager → Feeds → Add data source → Use bulk feed → paste <code className="text-[11px]">{site}/api/feeds/meta-catalogue.csv?locale=en&amp;currency=EUR</code></li>
          <li>3. <b>Google:</b> <a href="https://merchants.google.com" target="_blank" rel="noreferrer" className="underline decoration-brass">merchants.google.com</a> → Products → Feeds → Country <b>Morocco</b>, Language <b>English</b>. Method: <b>Scheduled fetch, daily</b>.</li>
          <li>4. Fetch URL: <code className="text-[11px]">{site}/api/feeds/google-merchant.xml?locale=en&amp;currency=EUR</code> (repeat with locale=fr, es, ar).</li>
          <li>5. Every item ships to <b>MA · FR · BE · NL · ES · GB · US · CA · DE · IT</b> automatically.</li>
          <li>6. <b>TikTok catalogue:</b> Catalog Manager → Add products → Data feed → URL <code className="text-[11px]">{site}/api/feeds/tiktok-catalogue.csv?locale=en&amp;currency=EUR</code>, schedule daily, currency EUR.</li>
          <li>7. <b>Pinterest / Snapchat catalogues:</b> add the Meta CSV URL as a scheduled data source.</li>
        </ol>
      </Panel>
    </div>
  );
}

function ResetTab({ settings, onOpen }) {
  return (
    <Panel title="Reset for launch" testid="reset-panel" action={<Rocket size={16} className="text-brass" />}>
      <p className="text-sm text-danger flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        This wipes every test order, buyer account, subscription, payout, activity log and gateway event.
        You keep the ops team, roles, settings, cities, zones, occasions and one demo florist to showcase the app.
      </p>
      <p className="text-xs text-muted">Run this once, right before your public launch.</p>
      <button className="btn-primary w-full sm:w-fit" data-testid="reset-open" onClick={onOpen}>Open launch reset preview…</button>
    </Panel>
  );
}

function ResetModal({ reset, setReset, onDone, setMsg }) {
  const [busy, setBusy] = useState(false);
  const armed = reset.phrase === "RESET FOR LAUNCH";
  const run = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/admin/reset/production", { confirm: reset.phrase, keep_florist_id: reset.keep_florist_id });
      setMsg(`Reset complete. Kept ${data.kept_florist?.shop_name || "no florist"}. Deployed data is now clean.`);
      onDone();
    } catch (e) { setMsg(errText(e)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 scrim z-40 flex items-center justify-center p-6" onClick={() => setReset(null)} data-testid="reset-modal">
      <div className="bg-plaster w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-brass" onClick={(e) => e.stopPropagation()}>
        <div className="bg-ink text-plaster px-6 py-4 flex items-center gap-3">
          <Rocket size={18} className="text-brass" />
          <h2 className="font-display text-2xl">Launch reset — dry run</h2>
          <button className="ms-auto" onClick={() => setReset(null)}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="smallcaps">Will be wiped</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mt-2 text-xs">
              {Object.entries(reset.counts).map(([k, n]) => (
                <div key={k} className={`border p-2 ${n ? "border-danger/30 bg-danger/5" : "border-brass-light"}`}>
                  <p className="text-muted">{k}</p><p className="tabular text-ink font-medium">{n}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="smallcaps">Keep this florist (with products & reviews)</p>
            <select className="field mt-2" data-testid="reset-keep-florist" value={reset.keep_florist_id}
                    onChange={(e) => setReset({ ...reset, keep_florist_id: e.target.value })}>
              <option value="">— no florist (fully clean) —</option>
              {reset.florists.map((f) => <option key={f.id} value={f.id}>{f.shop_name}</option>)}
            </select>
          </div>

          <div className="bg-danger/10 border border-danger/30 p-4">
            <p className="text-sm text-danger flex items-center gap-2"><AlertTriangle size={14} /> Type <code>RESET FOR LAUNCH</code> to confirm.</p>
            <input className="field mt-2 font-mono" placeholder="RESET FOR LAUNCH" data-testid="reset-phrase"
                   value={reset.phrase} onChange={(e) => setReset({ ...reset, phrase: e.target.value })} />
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost text-xs" onClick={() => setReset(null)} data-testid="reset-cancel">Cancel</button>
            <button className="btn-primary" data-testid="reset-confirm" disabled={!armed || busy} onClick={run}>
              {busy ? "Wiping…" : "Reset the database now"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
